import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectProducts } from "../../../store/selectors/ProductSelector";
import { useTranslation } from "react-i18next";
import { FaArrowLeft, FaEdit } from "react-icons/fa";
import {
  Button,
  IconButton,
  Box,
  Text,
  Flex,
  Image,
  Badge,
  Divider,
  VStack,
  HStack,
} from "@chakra-ui/react";
import { Product } from "../../../entity/Product";
import { useState } from "react";
import VEditorPreview from "../../components/veditor/VEditorPreview";

const ProductDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const products = useSelector(selectProducts);

  // Find the product with the matching ID
  const product = products.find((p: Product) => p.id === id);

  const [currentImageUrl, setCurrentImageUrl] = useState<string | undefined>(
    product?.imageUrl[0],
  );

  if (!product) {
    return (
      <Box p={5}>
        <Text>{t("product.not_found")}</Text>
        <Button leftIcon={<FaArrowLeft />} mt={4} onClick={() => navigate(-1)}>
          {t("buttons.back")}
        </Button>
      </Box>
    );
  }

  if (!product) {
    return (
      <Box p={5}>
        <Text>{t("product.not_found")}</Text>
        <Button leftIcon={<FaArrowLeft />} mt={4} onClick={() => navigate(-1)}>
          {t("buttons.back")}
        </Button>
      </Box>
    );
  }

  return (
    <Box p={5} maxW="1200px" mx="auto">
      <Button
        leftIcon={<FaArrowLeft />}
        mb={6}
        variant="outline"
        onClick={() => navigate(-1)}
      >
        {t("buttons.back")}
      </Button>

      <Box bg="white" borderRadius="lg" boxShadow="sm" p={6}>
        <Flex direction={{ base: "column", md: "row" }} gap={8}>
          <Box flex={1}>
            <Box
              bg="gray.100"
              borderRadius="md"
              p={8}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              minH="400px"
            >
              {product.imageUrl && product.imageUrl.length > 0 ? (
                <Image
                  src={currentImageUrl || product.imageUrl[0]} // Show first image as main
                  alt={product.name}
                  maxH="350px"
                  objectFit="contain"
                  mb={4}
                />
              ) : (
                <Image
                  src="/placeholder-product.png"
                  alt="No image available"
                  maxH="350px"
                  objectFit="contain"
                  mb={4}
                />
              )}

              {/* Thumbnail gallery */}
              {product.imageUrl && product.imageUrl.length > 1 && (
                <HStack spacing={2} mt={4} overflowX="auto" py={2} w="100%">
                  {product.imageUrl.map((img, index) => (
                    <Box
                      key={index}
                      border="2px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      p={1}
                      cursor="pointer"
                      _hover={{ borderColor: "blue.500" }}
                      onClick={() => setCurrentImageUrl(img)}
                    >
                      <Image
                        src={img}
                        alt={`${product.name} ${index + 1}`}
                        boxSize="50px"
                        objectFit="cover"
                        borderRadius="sm"
                      />
                    </Box>
                  ))}
                </HStack>
              )}
            </Box>
          </Box>

          {/* Product Details */}
          <Box flex={1}>
            <Flex justify="space-between" align="flex-start" mb={4}>
              <Box>
                <Text fontSize="2xl" fontWeight="bold" mb={2}>
                  {product.name}
                </Text>
                <HStack spacing={4} mb={4}>
                  <Badge
                    colorScheme={product.quantity > 0 ? "green" : "red"}
                    px={3}
                    py={1}
                    borderRadius="full"
                    fontSize="0.9em"
                  >
                    {product.quantity > 0
                      ? t("product.in_stock")
                      : t("product.out_of_stock")}
                  </Badge>
                  <Text color="gray.600">
                    {product.quantity} {t("product.available")}
                  </Text>
                </HStack>
              </Box>
              <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                ${product.price.toFixed(2)}
              </Text>
            </Flex>

            <Divider my={4} />

            {/* Product Description */}
            <VStack align="stretch" spacing={4}>
              <Box>
                <Text fontWeight="semibold" mb={2}>
                  {t("product.description")}:
                </Text>
                <Text color="gray.700" whiteSpace="pre-line">
                  <VEditorPreview html={product.description} />
                </Text>
              </Box>
              {product.packs && product.packs.length > 0 && (
                <Box>
                  <Text fontWeight="semibold" mb={3}>
                    {t("product.pack_offers")}:
                  </Text>
                  <VStack align="stretch" spacing={3}>
                    {product.packs.map((pack, index) => (
                      <Box
                        key={index}
                        border="1px solid"
                        borderColor="gray.200"
                        borderRadius="md"
                        p={4}
                        bg="gray.50"
                      >
                        <Flex justify="space-between" align="center">
                          <Box>
                            <Text fontWeight="medium" fontSize="lg" mb={1}>
                              {pack.name}
                            </Text>
                            {pack.quantity && (
                              <Text color="gray.600" fontSize="sm">
                                {pack.quantity} {t("product.items")}
                              </Text>
                            )}
                          </Box>
                          <Box textAlign="right">
                            <Text
                              fontWeight="bold"
                              fontSize="lg"
                              color="blue.600"
                            >
                              {pack.price.toFixed(2)}
                            </Text>
                            {pack.discount && (
                              <Text color="green.600" fontSize="sm">
                                {t("product.save")} {pack.discount}%
                              </Text>
                            )}
                          </Box>
                        </Flex>
                        {pack.deliveryPrice !== undefined && (
                          <Text color="gray.600" fontSize="sm" mt={2}>
                            {t("product.delivery")}:{" "}
                            {pack.deliveryPrice.toFixed(2)}
                          </Text>
                        )}
                      </Box>
                    ))}
                  </VStack>
                </Box>
              )}

              {/* Additional Product Information */}
              <Box>
                <Text fontWeight="semibold" mb={2}>
                  {t("product.details")}:
                </Text>
                <VStack align="stretch" spacing={2}>
                  <Flex justify="space-between">
                    <Text color="gray.600">{t("product.category")}:</Text>
                    <Text>{product.name || "—"}</Text>
                  </Flex>
                  <Flex justify="space-between">
                    <Text color="gray.600">{t("product.reference")}:</Text>
                    <Text>{product.ref || "—"}</Text>
                  </Flex>
                  <Flex justify="space-between">
                    <Text color="gray.600">{t("product.created_at")}:</Text>
                    <Text>
                      {new Date(
                        product.createdAt || new Date(),
                      ).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </Text>
                  </Flex>
                </VStack>
              </Box>
            </VStack>

            <Divider my={6} />
          </Box>
        </Flex>
      </Box>
    </Box>
  );
};

export default ProductDetails;
